const ReadAloudSynth = require("./ReadAloudSynth");
const ReadAloudElevenLabs = require("./ReadAloudElevenLabs");

/**
 * A small class that abstracts the speech API. Only this file should directly
 * interact with those APIs.
 */

/**
 * Constructor.
 *
 * Uses ElevenLabs as primary provider with browser speech synthesis fallback.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const ReadAloudContext = function(voicesLoadedCB) {
	this.readAloudSynth = new ReadAloudSynth();
	this.readAloudElevenLabs = new ReadAloudElevenLabs();
	this.activeProvider = null; // tracks which provider is currently playing
	this.playing = false;
	this.paused = false;
	this.lastUtterance = null;
};

/**
 * Cleanup method. Memory is leaked if you forget about a ReadAloudContext
 * instance without calling this method.
 *
 * @returns {object}
 */
ReadAloudContext.prototype.destroy = function() {
	this.readAloudSynth.destroy();
	this.readAloudElevenLabs.destroy();
};

/**
 * @returns {boolean}
 */
ReadAloudContext.prototype.isSpeaking = function() {
	return (
		this.readAloudSynth.isSpeaking() || this.readAloudElevenLabs.isSpeaking()
	);
};

/**
 * @returns {boolean}
 */
ReadAloudContext.prototype.isPaused = function() {
	return this.readAloudSynth.isPaused() || this.readAloudElevenLabs.isPaused();
};

/**
 * @returns {boolean}
 */
ReadAloudContext.prototype.isPending = function() {
	return (
		this.readAloudSynth.isPending() || this.readAloudElevenLabs.isPending()
	);
};

ReadAloudContext.prototype.cancel = function() {
	this.readAloudSynth.cancel();
	this.readAloudElevenLabs.cancel();
	this.activeProvider = null;
};

ReadAloudContext.prototype.pause = function() {
	this.readAloudSynth.pause();
	this.readAloudElevenLabs.pause();
};

ReadAloudContext.prototype.resume = function() {
	if (this.readAloudElevenLabs.isPaused()) {
		this.readAloudElevenLabs.resume();
	} else if (this.readAloudSynth.isPaused()) {
		this.readAloudSynth.resume();
	}
};

/**
 * Registers a listener func for when playing value updates.
 */
ReadAloudContext.prototype.onStatusChange = function(listenerFunc) {
	this.readAloudSynth.onStatusChange(listenerFunc);
	this.readAloudElevenLabs.onStatusChange(listenerFunc);
};

/**
 * Internal helper for updating the internal playing value and
 * firing off the event listeners.
 */
ReadAloudContext.prototype.updateStatus = function(newStatus = {}) {
	this.readAloudSynth.updateStatus(newStatus);
	this.readAloudElevenLabs.updateStatus(newStatus);
};

/**
 * This actually triggers audio in the speech API. Pass the text you want to
 * be spoken. Tries ElevenLabs first, falls back to browser speech synthesis.
 *
 * It is an error to pass no text, though this method will log to ErrorUtil
 * instead of throwing.
 *
 * @param {string} text
 * @returns {boolean}
 */
// eslint-disable-next-line @lwc/lwc/no-async-await
ReadAloudContext.prototype.speak = async function(text) {
	// Try ElevenLabs first
	const elevenLabsSuccess = await this.readAloudElevenLabs.speak(text);

	if (elevenLabsSuccess) {
		this.activeProvider = "elevenlabs";
		return true;
	}

	// Fall back to browser speech synthesis
	this.activeProvider = "synth";
	return this.readAloudSynth.speak(text);
};

/**
 * Preloads audio for the given text without playing it.
 * Only preloads via ElevenLabs (the primary provider).
 * Useful for preloading next problem's audio on problem switch.
 *
 * @param {string} text
 * @returns {Promise<boolean>} - resolves true if audio is ready
 */
ReadAloudContext.prototype.preload = function(text) {
	return this.readAloudElevenLabs.preload(text);
};

module.exports = ReadAloudContext;
